import { WorkawareApi } from 'apis/WorkawareApi'
import { transform, TransformMethods } from 'data/transform'
import { TrackPositionDto } from '../dtos/TrackPositionDto'

export class trackingCurrentPosition {
  private _api: WorkawareApi
  private _trackingCurrentPosition: TransformMethods<TrackPositionDto>

  constructor(api: WorkawareApi) {
    this._api = api
    this._trackingCurrentPosition = transform<TrackPositionDto>(TrackPositionDto)
  }

  getUserTrackingCurrentPositions = async () => {
    const result = await this._api.trackingCurrentPosition.getTrackingUser()
    return this._trackingCurrentPosition.arrayFrom(result)
  }

  getUserHistoricPositions = async (data) => {
    const result = await this._api.trackingCurrentPosition.getHistoricPositions(data)
    return this._trackingCurrentPosition.arrayFrom(result)
  }

  getCompletedCheckpointHistory = async (data): Promise<CompletedCheckpointHistoryItem[]> => {
    const result = await this._api.trackingCurrentPosition.getCompletedCheckpointHistory(data)
    return result || []
  }

  // Live progress for each requested journey's currently-InProgress checkpoint, sourced
  // from the real JourneyCheckins log -- NOT TrackingCurrentPosition/TrackingPositionHistory
  // (those stay wired to getUserTrackingCurrentPositions/getUserHistoricPositions above,
  // unchanged). Journeys with no InProgress checkpoint, or none the caller can see, are
  // simply absent from the result.
  getInProgressCheckpointProgress = async (journeyIds: number[]): Promise<InProgressCheckpointProgressItem[]> => {
    if (!journeyIds || journeyIds.length === 0) return []
    const result = await this._api.trackingCurrentPosition.getInProgressCheckpointProgress({ JourneyIds: journeyIds })
    return result || []
  }

  // Distinct UserIds who have arrived at least one checkpoint (JourneyCheckpointUserStatuses),
  // regardless of whether they currently have a live GPS position -- used so the People picker
  // on the history view can offer a user who has checkpoint history but isn't online right now.
  getUsersWithCheckpointHistory = async (): Promise<number[]> => {
    const result = await this._api.trackingCurrentPosition.getUsersWithCheckpointHistory()
    return result || []
  }

  // Progress of every user assigned to one checkpoint, whatever its status -- each one's own
  // status, when they started/arrived, and the route they actually travelled (JourneyCheckins).
  // A non-admin only ever gets themselves back. Rejects with 404 for a checkpoint the caller
  // cannot see.
  getCheckpointUsersProgress = async (checkpointId: number): Promise<CheckpointUserProgress[]> => {
    const result = await this._api.trackingCurrentPosition.getCheckpointUserProgress({ CheckpointId: checkpointId })
    return result || []
  }

}

// Shape of each item returned by /api/v2/journeys/checkpointHistory -- one fully-arrived
// checkpoint, sourced from the real JourneyCheckins log (not raw GPS pings).
export interface CompletedCheckpointHistoryItem {
  checkpointId: number
  checkpointName?: string
  journeyId: number
  journeyName?: string
  /** Whose arrival this entry is -- the same checkpoint can appear once per assigned
   *  user, since each user's own arrival is tracked independently. */
  userId: number
  userName?: string
  departureLocation?: string
  arrivalLocation?: string
  departurePointLatitude?: number
  departurePointLongitude?: number
  arrivalPointLatitude?: number
  arrivalPointLongitude?: number
  startedOn?: string | null
  arrivedOn: string
  durationMinutes?: number | null
  /** The route actually travelled: every positioned JourneyCheckins row logged for
   *  this checkpoint (plus the journey-level ones logged between starting it and
   *  arriving), in chronological order. Distinct from the departure/arrival fields
   *  above, which are the checkpoint's PLANNED points. */
  track?: CompletedCheckpointTrackPoint[]
}

/** One positioned check-in from the JourneyCheckins log. */
export interface CompletedCheckpointTrackPoint {
  latitude: number
  longitude: number
  checkInOn: string
  command?: string
}

// Shape of each item returned by /api/v2/journeys/checkpointProgress -- one journey's
// currently-InProgress checkpoint, with where the assigned user has reached and how far
// they've travelled so far, sourced from the real JourneyCheckins log.
export interface InProgressCheckpointProgressItem {
  journeyId: number
  checkpointId: number
  checkpointName?: string
  userId: number
  travelMethodId?: number
  startedOn?: string | null
  distanceTravelledMeters: number
  lastPosition: {
    latitude: number
    longitude: number
    checkInOn: string
    heading?: number
    speed?: number
  }
}

// Shape of /api/v2/journeys/checkpointUserProgress.
export interface CheckpointUserProgress {
  journeyId: number
  checkpointId: number
  userId: number
  statusId: number
  travelMethodId?: number
  startedOn?: string | null
  arrivedOn?: string | null
  distanceTravelledMeters: number
  /** Time-ordered positioned check-ins -- the route actually travelled. */
  track: { latitude: number; longitude: number; checkInOn: string; command?: string }[]
  lastPosition: { latitude: number; longitude: number; checkInOn: string; heading?: number; speed?: number } | null
}
